import { useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { LevelPlayground } from '@/features/tutorial/LevelPlayground';
import reactStarterData from '@/data/templates/react-starter.json';
import nodejsApiData from '@/data/templates/nodejs-api.json';
import pythonScriptData from '@/data/templates/python-script.json';
import documentAnalysisData from '@/data/templates/document-analysis.json';

interface Template {
  id: string;
  name: string;
  description: string;
  language: string;
  framework: string;
  difficulty: string;
  icon: string;
}

const templates: Template[] = [
  {
    id: 'react-starter',
    name: 'React App Starter',
    description: 'A simple React application with components, hooks, and routing.',
    language: 'JavaScript',
    framework: 'React',
    difficulty: 'beginner',
    icon: '⚛️',
  },
  {
    id: 'nodejs-api',
    name: 'Node.js REST API',
    description: 'A RESTful API built with Express.js with CRUD operations.',
    language: 'JavaScript',
    framework: 'Express',
    difficulty: 'intermediate',
    icon: '🟢',
  },
  {
    id: 'python-script',
    name: 'Python Data Script',
    description: 'A Python script for data processing and analysis.',
    language: 'Python',
    framework: 'Python',
    difficulty: 'beginner',
    icon: '🐍',
  },
  {
    id: 'document-analysis',
    name: 'Document Analysis with Claude',
    description: 'Practice analyzing documents with Claude AI - no coding required.',
    language: 'Text',
    framework: 'Claude AI',
    difficulty: 'beginner',
    icon: '📄',
  },
];

export default function SandboxPage() {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const [templateData, setTemplateData] = useState<any>(null);
  const [savedSessions, setSavedSessions] = useState<any[]>([]);

  useEffect(() => {
    // Load template data
    if (templateId) {
      loadTemplateData(templateId);
    }

    // Load saved sessions from localStorage
    const sessions = JSON.parse(localStorage.getItem('sandbox-sessions') || '[]');
    setSavedSessions(sessions);
  }, [templateId]);

  const loadTemplateData = (id: string) => {
    switch (id) {
      case 'react-starter':
        setTemplateData(reactStarterData);
        break;
      case 'nodejs-api':
        setTemplateData(nodejsApiData);
        break;
      case 'python-script':
        setTemplateData(pythonScriptData);
        break;
      case 'document-analysis':
        setTemplateData(documentAnalysisData);
        break;
      default:
        setTemplateData(null);
    }
  };

  const saveSession = () => {
    if (!templateData) return;

    const session = {
      id: Date.now(),
      templateId,
      templateName: templateData.name,
      timestamp: new Date().toISOString(),
      // In a real implementation, we'd save the current editor state
    };

    const sessions = JSON.parse(localStorage.getItem('sandbox-sessions') || '[]');
    sessions.push(session);
    localStorage.setItem('sandbox-sessions', JSON.stringify(sessions));
    setSavedSessions(sessions);
  };

  const deleteSession = (sessionId: number) => {
    const sessions = savedSessions.filter((s) => s.id !== sessionId);
    localStorage.setItem('sandbox-sessions', JSON.stringify(sessions));
    setSavedSessions(sessions);
  };

  // Template selection screen
  if (!templateId) {
    return (
      <div className="min-h-screen p-8 bg-black">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/')}
              className="mb-4"
            >
              ← Back to Home
            </Button>
            <h1 className="text-4xl font-bold mb-2 text-primary-400">
              Practice Mode
            </h1>
            <p className="text-primary-200">
              Practice freely with project templates - no time limits, no scoring
            </p>
          </div>

          {/* Templates */}
          <div className="mb-12">
            <h2 className="text-2xl font-semibold mb-4 text-primary-300">
              Choose a Template
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {templates.map((template) => (
                <Card
                  key={template.id}
                  variant="elevated"
                  className="hover:border-primary-500/50 cursor-pointer transition-all duration-300 hover:scale-105"
                  onClick={() => navigate(`/sandbox/${template.id}`)}
                >
                  <CardHeader>
                    <div className="text-5xl mb-3">{template.icon}</div>
                    <Badge
                      variant={
                        template.difficulty === 'beginner'
                          ? 'success'
                          : template.difficulty === 'intermediate'
                          ? 'warning'
                          : 'error'
                      }
                      size="sm"
                      className="mb-2"
                    >
                      {template.difficulty}
                    </Badge>
                    <CardTitle className="text-lg">{template.name}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-primary-300 mb-4">
                      {template.description}
                    </p>
                    <div className="space-y-2 mb-4 text-sm">
                      <div className="flex justify-between text-primary-300">
                        <span>Language:</span>
                        <span className="text-primary-400">{template.language}</span>
                      </div>
                      <div className="flex justify-between text-primary-300">
                        <span>Framework:</span>
                        <span className="text-primary-400">{template.framework}</span>
                      </div>
                    </div>
                    <Button variant="primary" size="sm" className="w-full">
                      Start Practicing
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Saved Sessions */}
          {savedSessions.length > 0 && (
            <div>
              <h2 className="text-2xl font-semibold mb-4 text-primary-300">
                Saved Sessions
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {savedSessions.map((session) => (
                  <Card key={session.id} variant="default">
                    <CardHeader>
                      <CardTitle className="text-base">
                        {session.templateName}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-primary-300 mb-4">
                        {new Date(session.timestamp).toLocaleString()}
                      </p>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => navigate(`/sandbox/${session.templateId}`)}
                        >
                          Load
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => deleteSession(session.id)}
                        >
                          Delete
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Loading state
  if (!templateData) {
    return (
      <div className="min-h-screen p-8 bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-primary-200">Loading template...</p>
        </div>
      </div>
    );
  }

  // Template playground
  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <div className="border-b border-dark-200 bg-dark-50 p-4">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/sandbox')}
            >
              ← Back
            </Button>
            <div>
              <h2 className="text-lg font-semibold text-primary-400">
                {templateData.name}
              </h2>
              <p className="text-sm text-primary-300">
                {templateData.framework} • {templateData.language}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={saveSession}>
              💾 Save Session
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => window.location.reload()}
            >
              🔄 Reset
            </Button>
          </div>
        </div>
      </div>

      {/* Info Panel */}
      <div className="border-b border-dark-200 bg-dark-50 p-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card variant="default" className="bg-dark-100">
              <CardHeader>
                <CardTitle className="text-base">About This Template</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-primary-300">
                  {templateData.description}
                </p>
              </CardContent>
            </Card>
            <Card variant="default" className="bg-dark-100">
              <CardHeader>
                <CardTitle className="text-base">Practice Ideas</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-1 text-sm text-primary-300">
                  {templateData.suggestedTasks.slice(0, 3).map((task: string, i: number) => (
                    <li key={i}>• {task}</li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Playground */}
      <LevelPlayground
        starterCode={templateData.files}
        onAction={(action, data) => {
          console.log('Sandbox action:', action, data);
        }}
      />
    </div>
  );
}
